// galaxy/camera.js: the camera path, a pure function of (progress, aspect, look).
// Beats (progress):
//   0.00-0.03  at the Sun (0.1 pc out): the real sky (ESO panorama + HYG stars)
//   0.03-0.38  rise: log distance 0.1 pc -> ~40 kpc, from in-plane to face-on
//   0.38-0.60  face-on barred spiral, "You are here" (slow push + turn)
//   0.60-0.97  dive to the core (oblique, bar near broadside); hold to 1.0
import * as THREE from 'three';
import { clamp, lerp, seg } from '../lib/common.js';
import { SUN, D2R } from './model.js';

const LN = Math.log;
const sst = (x) => x * x * (3 - 2 * x);
// slow the log zoom around ~1.3 kpc, where the disk opens up under the camera (no lurch there)
const warp = (u) => u - (0.5 * Math.sin(2 * Math.PI * (u - 0.5))) / (2 * Math.PI);
// 0..1 with eased ends and a long constant-speed middle (a steady log zoom)
function cruise(x, k = 0.25) {
  x = clamp(x, 0, 1);
  const n = 1 / (1 - k);
  if (x < k) return (n * x * x) / (2 * k);
  if (x > 1 - k) return 1 - (n * (1 - x) * (1 - x)) / (2 * k);
  return n * (x - k / 2);
}

// The rise, re-eased by the measured on-screen change (frame MAD per 0.01 p, tools/galaxy_diff.py):
// (four passes of MAD equalisation; 0.25 of the time kept as a floor so the sky start eases in)
// c(x) at x = i/60 spreads the old 0.27-0.36 rush (MAD 0.098/step) over the rise. c = the old cruise() value.
const RISE_C = [0.0000, 0.0000, 0.0001, 0.0002, 0.0062, 0.0775, 0.1988, 0.2325, 0.2647, 0.2946, 0.3265, 0.3610, 0.4024, 0.4575, 0.5643, 0.6329, 0.6659, 0.6888, 0.7070, 0.7223, 0.7359, 0.7525, 0.7759, 0.8022, 0.8291, 0.8534, 0.8710, 0.8835, 0.8921, 0.8983, 0.9036, 0.9082, 0.9126, 0.9168, 0.9210, 0.9250, 0.9289, 0.9327, 0.9364, 0.9403, 0.9442, 0.9481, 0.9520, 0.9561, 0.9604, 0.9649, 0.9694, 0.9743, 0.9793, 0.9842, 0.9884, 0.9918, 0.9947, 0.9968, 0.9983, 0.9994, 0.9999, 1.0000, 1.0000, 1.0000, 1.0000];
function riseC(x) { x = clamp(x, 0, 1) * (RISE_C.length - 1); const i = Math.min(Math.floor(x), RISE_C.length - 2); return lerp(RISE_C[i], RISE_C[i + 1], x - i); }

export const BEATS = { rise0: 0.03, face0: 0.38, face1: 0.60, dive1: 0.97 };
export const TUNE = { e0: 13, e1: 16, eD0: 1.0, oy0: 0.17, tD0: 8, Dend: 7.5, elEnd: 12, azEnd: -12, fovEnd: 30 };

const _f = new THREE.Vector3(), _r = new THREE.Vector3(), _u = new THREE.Vector3(), _b = new THREE.Vector3();
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _d = new THREE.Vector3();
const TF = new THREE.Vector3(0, 0, 1.1);

export function makeCamState() {
  return { pos: new THREE.Vector3(), target: new THREE.Vector3(), quat: new THREE.Quaternion(), fov: 44, oy: 0, Ds: 0, D: 1, el: 0, portrait: false };
}

function orbit(out, T, D, el, az) {
  const ce = Math.cos(el * D2R), se = Math.sin(el * D2R), ca = Math.cos(az * D2R), sa = Math.sin(az * D2R);
  out.target.copy(T);
  out.pos.set(T.x + D * sa * ce, T.y + D * se, T.z + D * ca * ce);
  _r.set(ca, 0, -sa);
}

export function evalCamera(p, aspect, look, out, T = TUNE) {
  const portrait = aspect < 0.85;
  const fovF = portrait ? 56 : 44, fovS = portrait ? 78 : 68;
  let fov = fovF;
  const tanV = Math.tan((fovF * D2R) / 2);
  const Rfit = portrait ? 13.2 : 13.6; // portrait: the frayed outer arms (to ~16 kpc) stay inside the frame
  const Dface = Math.max(Rfit / tanV, Rfit / (Math.max(aspect, 0.3) * tanV));
  let oy = 0;

  if (p <= BEATS.face0) {
    // rise: camera = SUN + Ds (0, sin e, cos e), looking at a target that slides Sun -> TF
    const DsEnd = Math.hypot(Dface, SUN.z - TF.z);
    const eEnd = Math.atan2(Dface, TF.z - SUN.z) / D2R; // > 90: over the centre side
    // 30% of the rise for 0.1 pc -> 50 pc, 70% for 50 pc -> the face-on view
    const c = riseC(seg(p, BEATS.rise0, BEATS.face0));
    const lnDs = c < 0.22 ? lerp(LN(1.0e-4), LN(0.05), c / 0.22) : lerp(LN(0.05), LN(DsEnd), warp((c - 0.22) / 0.78));
    const Ds = Math.exp(lnDs);
    // stay low (the band across the upper frame, the Sun below) until ~1.5 kpc, then climb over the disk
    // tilt down onto the disk while the photo hands over to the model (no grazing views of the model)
    const e1 = lerp(T.e0, T.e1, sst(seg(lnDs, LN(0.08), LN(T.eD0))));
    const e = lerp(e1, eEnd, sst(seg(lnDs, LN(T.eD0), LN(DsEnd))));
    const wT = sst(seg(lnDs, LN(T.tD0), LN(DsEnd)));
    out.pos.set(0, SUN.y + Ds * Math.sin(e * D2R), SUN.z + Ds * Math.cos(e * D2R));
    out.target.copy(SUN).lerp(TF, wT);
    oy = T.oy0 * (1 - sst(seg(lnDs, LN(T.tD0 * 0.6), LN(DsEnd))));
    _r.set(1, 0, 0);
    out.Ds = Ds;
    fov = lerp(fovS, fovF, sst(seg(lnDs, LN(0.02), LN(3.0))));
  } else {
    // face-on hold, then the dive: orbit a target sliding TF -> centre
    const h = seg(p, BEATS.face0, BEATS.face1);
    const dv = cruise(seg(p, BEATS.face1, BEATS.dive1), 0.3);
    let lnD = LN(Dface) - 0.12 * sst(h);
    lnD = lerp(lnD, LN(T.Dend * (portrait ? 1.35 : 1)), dv);
    const el = lerp(90 - 4 * sst(h), T.elEnd, sst(seg(dv, 0.0, 0.85)));
    const az = lerp(-6 * sst(h), T.azEnd, sst(seg(dv, 0.05, 1.0)));
    _d.copy(TF).multiplyScalar(1 - sst(seg(dv, 0.0, 0.6)));
    // telephoto at the end: from outside the dust layer the bar is seen end-on (Sun-like view): a compact bulge
    fov = lerp(fovF, portrait ? T.fovEnd * 1.45 : T.fovEnd, sst(seg(dv, 0.35, 1.0)));
    orbit(out, _d, Math.exp(lnD), el, az);
    out.Ds = out.pos.distanceTo(SUN);
  }
  if (portrait) oy -= 0.07; // subject in the upper-middle on phones

  // basis: forward to the target, right kept horizontal
  _f.copy(out.target).sub(out.pos).normalize();
  _u.crossVectors(_r, _f).normalize();
  _r.crossVectors(_f, _u).normalize();
  _b.copy(_f).negate();
  _m.makeBasis(_r, _u, _b);
  out.quat.setFromRotationMatrix(_m);

  // parallax: orbit the target a little (about screen up / right)
  const lx = clamp((look && look.x) || 0, -1, 1), ly = clamp((look && look.y) || 0, -1, 1);
  if (lx || ly) {
    _q.setFromAxisAngle(_u, -lx * 3.0 * D2R);
    _q2.setFromAxisAngle(_r, ly * 2.0 * D2R);
    _q.multiply(_q2);
    _d.copy(out.pos).sub(out.target).applyQuaternion(_q);
    out.pos.copy(out.target).add(_d);
    out.quat.premultiply(_q);
  }
  out.D = out.pos.distanceTo(out.target);
  out.fov = fov; out.oy = oy; out.portrait = portrait;
  return out;
}

// state + screen shift (fractions of the frame; +x right, +y down) -> camera
export function applyCamera(camera, st, shiftX, shiftY) {
  camera.position.copy(st.pos);
  camera.quaternion.copy(st.quat);
  camera.fov = st.fov;
  camera.near = Math.max(1e-7, Math.min(st.Ds, st.D) * 0.02);
  camera.far = 500;
  camera.updateProjectionMatrix();
  const e = camera.projectionMatrix.elements;
  e[8] -= 2 * shiftX;
  e[9] += 2 * (shiftY + st.oy);
  camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();
  camera.updateMatrixWorld(true);
}
